/**
 * agentSuggestions — research-backed typed Suggestions for /design.
 *
 * Each detector reads the live project graph and emits one or more
 * AgentProposals. Every Suggestion carries:
 *   · ruleId / ruleTitle / severity / fix   (human-readable in the rail)
 *   · wasInformedBy[]                        (citation tag — research / standard)
 *   · confidence                             (calibrated per detector)
 *
 * Citations referenced:
 *   · NKBA Kitchen Planning Guideline 5      (work-triangle ≤ 6.7 m)
 *   · IS 3646-1992 / IESNA RP-1              (lux per task type)
 *   · NBC 2016 Part 4 (India)                (egress / fire safety)
 *   · ANSI A117.1-2017                       (accessibility clearances)
 *   · ECBC 2017 (India)                      (envelope / SHGC)
 *   · ASHRAE STC 50                          (acoustic separation)
 *   · Vastu Shastra Manjari (P. K. Acharya)  (cultural compliance)
 *   · Forlizzi & DiSalvo, CHI 2007           ("Hospitable Robots" sightline)
 *
 * Reads `VITE_STYLE_COHESION_THRESHOLD` + thresholds from env per CLAUDE.md §1.
 */
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import type { AgentProposal } from "./types";
import { env } from "../env";
import codegen from "../../../../docs/ontology/codegen.json";

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

function skusInRoom(graph: ProjectGraph, roomId: string): GraphNode[] {
  const ids = new Set(
    graph.edges
      .filter((e) => e.kind === "IN_ROOM" && e.target === roomId)
      .map((e) => e.source),
  );
  return graph.nodes.filter((n) => n.kind === "SKU" && ids.has(n.id));
}

function newSuggestion(args: {
  readonly agentId: string;
  readonly confidence: number;
  readonly about: { readonly kind: string; readonly id: string };
  readonly ruleId: string;
  readonly ruleTitle: string;
  readonly severity: "info" | "warn" | "block";
  readonly fix: string;
  readonly citation: string;
  readonly nowIso: string;
}): AgentProposal {
  return {
    agentId: args.agentId,
    confidence: args.confidence,
    about: args.about,
    wasInformedBy: [args.citation, `rule/${args.ruleId}`],
    proposed: {
      kind: "update",
      nodeKind: args.about.kind,
      subjectId: args.about.id,
      attrs: {
        ruleId: args.ruleId,
        ruleTitle: args.ruleTitle,
        severity: args.severity,
        fix: args.fix,
        citation: args.citation,
      },
      ts: args.nowIso,
      origin: "agent",
    },
  };
}

/** Style cohesion — average SKU.styleMatch across a room.  Below threshold
 *  (VITE_STYLE_COHESION_THRESHOLD, e.g. 0.82) we surface a typed suggestion. */
export function detectStyleCohesion(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const skus = skusInRoom(graph, room.id);
  if (skus.length < 2) return [];
  const matches = skus.map((s) => attrNum(s, "styleMatch") ?? 0).filter((v) => v > 0);
  if (matches.length === 0) return [];
  const avg = matches.reduce((a, b) => a + b, 0) / matches.length;
  if (avg >= env.VITE_STYLE_COHESION_THRESHOLD) return [];
  return [newSuggestion({
    agentId: "agent-style-cohesion",
    confidence: 0.85,
    about: { kind: "Room", id: room.id },
    ruleId: "R-style-cohesion",
    ruleTitle: "Style cohesion below threshold",
    severity: "warn",
    fix: `Room "${room.label}" averages ${Math.round(avg * 100)}% style match (target ≥ ${Math.round(env.VITE_STYLE_COHESION_THRESHOLD * 100)}%). Substitute lowest-match SKU.`,
    citation: "graph/style-clustering",
    nowIso,
  })];
}

/** Budget pull — sum of BudgetLine.amount FOR_ROOM(room) vs project share. */
export function detectBudgetPull(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const project = graph.nodes.find((n) => n.kind === "Project");
  const cap = attrNum(project, "budgetCap") ?? 0;
  if (cap === 0) return [];
  const lines = graph.edges
    .filter((e) => e.kind === "FOR_ROOM" && e.target === room.id)
    .map((e) => graph.nodes.find((n) => n.id === e.source && n.kind === "BudgetLine"))
    .filter((x): x is GraphNode => Boolean(x));
  const spent = lines.reduce((s, l) => s + (attrNum(l, "amount") ?? 0), 0);
  const util = spent / cap;
  if (util < env.VITE_BUDGET_LINE_TARGET) return [];
  return [newSuggestion({
    agentId: "agent-budget-pull",
    confidence: 0.92,
    about: { kind: "Room", id: room.id },
    ruleId: "R-budget-pull",
    ruleTitle: "Room pulling outsized budget share",
    severity: util >= env.VITE_BUDGET_LINE_TARGET * 1.5 ? "block" : "warn",
    fix: `"${room.label}" uses ${Math.round(util * 100)}% of project cap (target ≤ ${Math.round(env.VITE_BUDGET_LINE_TARGET * 100)}% per room avg). Consider substitutes.`,
    citation: "budget/per-room-target",
    nowIso,
  })];
}

/** Lead-day risk — any SKU's leadDays exceeds env.VITE_LEAD_TIME_BUFFER_DAYS. */
export function detectLeadRisk(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const skus = skusInRoom(graph, room.id);
  const out: AgentProposal[] = [];
  for (const sku of skus) {
    const lead = attrNum(sku, "leadDays");
    if (lead === null) continue;
    // Surface only when lead crosses 4× the buffer (typical project window risk).
    const threshold = env.VITE_LEAD_TIME_BUFFER_DAYS * 4;
    if (lead < threshold) continue;
    out.push(newSuggestion({
      agentId: "agent-lead-risk",
      confidence: 0.90,
      about: { kind: "SKU", id: sku.id },
      ruleId: "R-lead-vs-milestone",
      ruleTitle: "Long lead time vs milestone risk",
      severity: lead >= threshold * 1.5 ? "block" : "warn",
      fix: `"${sku.label}" lead ${lead}d exceeds project buffer (${env.VITE_LEAD_TIME_BUFFER_DAYS}d × 4). Look for a SUBSTITUTES candidate.`,
      citation: "supply-chain/lead-buffer",
      nowIso,
    }));
  }
  return out;
}

/** Kitchen work-triangle — NKBA Guideline 5 — sum of legs (sink-stove,
 *  stove-fridge, fridge-sink) ≤ 6.7 m, no leg < 1.2 m or > 2.7 m.
 *  We surface a typed alert when the room is a kitchen but ontology field
 *  Room.workTriangleM is declared and out of band. The actual constraint is
 *  geometric and lands fully in B.1; this detector keys off the ontology field. */
export function detectKitchenWorkTriangle(room: GraphNode, nowIso: string): AgentProposal[] {
  if ((attrStr(room, "roomKind") ?? "").toLowerCase() !== "kitchen") return [];
  const wt = attrNum(room, "workTriangleM");
  if (wt === null) return [];
  if (wt <= 6.7 && wt >= 3.6) return [];
  return [newSuggestion({
    agentId: "agent-work-triangle",
    confidence: 0.94,
    about: { kind: "Room", id: room.id },
    ruleId: "R-NKBA-work-triangle",
    ruleTitle: "Kitchen work-triangle out of band",
    severity: "warn",
    fix: `Sum of sink + stove + fridge legs is ${wt.toFixed(1)} m. NKBA Guideline 5 recommends 3.6 ≤ Σ ≤ 6.7 m.`,
    citation: "NKBA-G5",
    nowIso,
  })];
}

/** Vastu — bathroom should NOT face NE (Ishanya); kitchen prefers SE (Agneya).
 *  Reads typed Placement.facing and Room.quadrant from the ontology. */
export function detectVastuMismatch(room: GraphNode, nowIso: string): AgentProposal[] {
  const kind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const quadrant = attrStr(room, "quadrant");
  if (!quadrant) return [];
  const out: AgentProposal[] = [];
  if (kind.includes("bath") && quadrant.toUpperCase() === "NE") {
    out.push(newSuggestion({
      agentId: "agent-vastu",
      confidence: 0.78,
      about: { kind: "Room", id: room.id },
      ruleId: "R-vastu-bath-NE",
      ruleTitle: "Bath placed in NE (Ishanya) quadrant",
      severity: "warn",
      fix: `Vastu Shastra Manjari avoids bath in NE — clients may push back. Consider moving to NW or W.`,
      citation: "Vastu-Acharya-Manjari",
      nowIso,
    }));
  }
  if (kind.includes("kitchen") && quadrant.toUpperCase() !== "SE") {
    out.push(newSuggestion({
      agentId: "agent-vastu",
      confidence: 0.72,
      about: { kind: "Room", id: room.id },
      ruleId: "R-vastu-kitchen-SE",
      ruleTitle: "Kitchen not in SE (Agneya) quadrant",
      severity: "info",
      fix: `Kitchen in ${quadrant} — Vastu Shastra Manjari prefers SE (Agneya). Confirm with client preference.`,
      citation: "Vastu-Acharya-Manjari",
      nowIso,
    }));
  }
  return out;
}

/** Missing typed Approval for a high-value SKU. */
export function detectMissingApproval(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const skus = skusInRoom(graph, room.id);
  const out: AgentProposal[] = [];
  for (const sku of skus) {
    const price = attrNum(sku, "price") ?? 0;
    if (price < env.VITE_HIGH_VALUE_THRESHOLD_INR) continue; // only high-value items (typed env, never magic number)
    const hasApproval = graph.edges.some(
      (e) => (e.kind === "APPROVED" || e.kind === "APPROVED_BY") && (e.source === sku.id || e.target === sku.id),
    );
    if (hasApproval) continue;
    out.push(newSuggestion({
      agentId: "agent-approval-watch",
      confidence: 0.88,
      about: { kind: "SKU", id: sku.id },
      ruleId: "R-high-value-approval",
      ruleTitle: "High-value SKU lacks recorded Approval",
      severity: "warn",
      fix: `"${sku.label}" is high-value but has no APPROVED edge yet. Open approval before procurement.`,
      citation: "PROV-O/approval",
      nowIso,
    }));
  }
  return out;
}

/** Spacing suggestions — designer + client both look at these constantly.
 *  Reads room dimensions (Room.minDimM, Room.areaSqM) + SKU count to predict
 *  whether the existing placements have enough clearance. Real geometric
 *  verification lands in B.1 (clearance predicate); this detector is the
 *  density-based pre-check that fires before geometry runs.
 *
 *  Standards cited:
 *    · NBC 2016 Part 4 §4.3.6  — habitable room min clear dim 2.4 m
 *    · ANSI A117.1-2017 §304   — turning radius 1.5 m clear floor
 *    · NKBA Guideline 6        — kitchen aisle ≥ 1.07 m (single cook) / 1.22 m (multi)
 *    · NKBA Guideline 17       — bedroom 0.6 m clear at foot of bed
 *
 *  Where Room.minDimM exists, surfaces a typed Suggestion when the
 *  predicted occupancy + min dim crosses the standard's threshold. */
export function detectSpacingDensity(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const kind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const minDim = attrNum(room, "minDimM");
  const area = attrNum(room, "areaSqM") ?? (attrNum(room, "area") ?? 0) * 0.092903;
  const skuCount = skusInRoom(graph, room.id).length;
  const out: AgentProposal[] = [];

  // Kitchen — NKBA Guideline 6 — single-cook aisle ≥ 1.07 m. If minDimM is
  // below that and there's a counter + island placed, the cook clearance
  // is statistically too tight regardless of where the placer puts things.
  if (kind.includes("kitchen") && minDim !== null && minDim < 1.07 && skuCount >= 2) {
    out.push(newSuggestion({
      agentId: "agent-spacing-kitchen-aisle",
      confidence: 0.93,
      about: { kind: "Room", id: room.id },
      ruleId: "R-NKBA-kitchen-aisle",
      ruleTitle: "Kitchen aisle below NKBA single-cook minimum",
      severity: "warn",
      fix: `"${room.label}" minimum dimension is ${minDim.toFixed(2)} m. NKBA Guideline 6 needs ≥ 1.07 m clear aisle (single cook) or 1.22 m (multi-cook). Consider relocating island to a wider room.`,
      citation: "NKBA-G6",
      nowIso,
    }));
  }

  // Bedroom — NKBA Guideline 17 — 0.6 m clear at foot of bed. We approximate
  // this via room area: a 130 sq-ft bedroom (12 m²) packed with bed +
  // wardrobe + dresser starts losing the foot clearance.
  if (kind.includes("bed") && area > 0 && area < 9 && skuCount >= 3) {
    out.push(newSuggestion({
      agentId: "agent-spacing-bedroom-foot",
      confidence: 0.82,
      about: { kind: "Room", id: room.id },
      ruleId: "R-NKBA-bedroom-foot",
      ruleTitle: "Bedroom may lose foot-of-bed clearance",
      severity: "warn",
      fix: `"${room.label}" is ${area.toFixed(1)} m² with ${skuCount} placements. NKBA G17 wants ≥ 0.6 m clear at foot of bed — consider removing one piece or moving the bed against the longer wall.`,
      citation: "NKBA-G17",
      nowIso,
    }));
  }

  // Habitable room min-dim — NBC 2016 Part 4 §4.3.6 — minimum 2.4 m for
  // habitable rooms (living/bedroom/dining/study). Anything below is a
  // statutory hard block.
  const isHabitable = ["living", "bedroom", "dining", "study", "kids"].some((k) => kind.includes(k));
  if (isHabitable && minDim !== null && minDim < 2.4) {
    out.push(newSuggestion({
      agentId: "agent-spacing-min-dim",
      confidence: 0.97,
      about: { kind: "Room", id: room.id },
      ruleId: "R-NBC-habitable-min-dim",
      ruleTitle: "Habitable room below NBC minimum dimension",
      severity: "block",
      fix: `"${room.label}" minimum dim is ${minDim.toFixed(2)} m — NBC 2016 Part 4 §4.3.6 requires ≥ 2.4 m for habitable rooms. Cannot occupy as ${kind}.`,
      citation: "NBC-4.3.6",
      nowIso,
    }));
  }

  // ── Pairwise collision detection — runs on the actual placed SKUs
  // (using their override coords + ontology footprints + rotation),
  // fires a typed Suggestion when two AABBs intersect. Closes the
  // gap the user flagged ("kitchen island overlapping counter, agent
  // says nothing"). The advisor only fires DURING drag; this fires
  // CONTINUOUSLY so standing overlaps never sit silently. */
  const placed = skusInRoom(graph, room.id);
  const cats = (codegen.categoryFootprints ?? []) as ReadonlyArray<{ category: string; wM: number; dM: number }>;
  const dims = new Map<string, { wM: number; dM: number }>(cats.map((c) => [c.category, { wM: c.wM, dM: c.dM }]));
  const fallback = dims.get("Item") ?? { wM: 0.8, dM: 0.8 };
  const positions = placed.map((s) => {
    const cat = attrStr(s, "category") ?? "Item";
    const d = dims.get(cat) ?? fallback;
    const yaw = attrNum(s, `posYaw_${room.id}`) ?? 0;
    const c = Math.abs(Math.cos(yaw)), si = Math.abs(Math.sin(yaw));
    return {
      sku: s,
      x: attrNum(s, `posX_${room.id}`) ?? 0,
      z: attrNum(s, `posZ_${room.id}`) ?? 0,
      halfW: (c * d.wM + si * d.dM) / 2,
      halfD: (si * d.wM + c * d.dM) / 2,
      placed: attrNum(s, `posX_${room.id}`) !== null,
    };
  });
  for (let i = 0; i < positions.length; i += 1) {
    for (let j = i + 1; j < positions.length; j += 1) {
      const a = positions[i]!, b = positions[j]!;
      // Skip pairs where one side hasn't been positioned yet (the SVG
      // anchor placer might collide with itself before the user drags).
      if (!a.placed && !b.placed) continue;
      const dx = Math.abs(a.x - b.x);
      const dz = Math.abs(a.z - b.z);
      const overlapX = a.halfW + b.halfW - dx;
      const overlapZ = a.halfD + b.halfD - dz;
      if (overlapX > 0.01 && overlapZ > 0.01) {
        const overlap = Math.min(overlapX, overlapZ);
        // Smallest-displacement resolution — push along the smaller
        // overlap axis with a 0.05 m clearance margin. We move B (the
        // later SKU in the list) because A is usually the anchor (counter,
        // bed). Direction = away from A's centre. Designer sees a typed
        // delta instead of "move it somewhere", so this is a usable
        // Neeraj-Bhatia recommendation, not a complaint.
        const margin = 0.05;
        const moveSku = b.placed ? b.sku.label : a.placed ? a.sku.label : b.sku.label;
        const target = b.placed ? b : a;
        const ref = target === b ? a : b;
        const pushAxis = overlapX < overlapZ ? "x" : "z";
        const pushAmt = (pushAxis === "x" ? overlapX : overlapZ) + margin;
        const dirSign = pushAxis === "x" ? Math.sign(target.x - ref.x) || 1 : Math.sign(target.z - ref.z) || 1;
        const dirWord = pushAxis === "x"
          ? (dirSign > 0 ? "east" : "west")
          : (dirSign > 0 ? "south" : "north");
        const newX = target.x + (pushAxis === "x" ? dirSign * pushAmt : 0);
        const newZ = target.z + (pushAxis === "z" ? dirSign * pushAmt : 0);
        out.push(newSuggestion({
          agentId: "agent-spacing-collision",
          confidence: 0.99,
          about: { kind: "Room", id: room.id },
          ruleId: "R-spatial-collision",
          ruleTitle: `${a.sku.label} overlaps ${b.sku.label} in ${room.label}`,
          severity: "block",
          fix: `Bounding boxes overlap by ${overlap.toFixed(2)} m (${Math.round(overlap * 100)} cm). Move "${moveSku}" ${pushAmt.toFixed(2)} m ${dirWord} (to posX ${newX.toFixed(2)} m, posZ ${newZ.toFixed(2)} m) to clear with ${margin.toFixed(2)} m margin, or rotate to align with the wall.`,
          citation: "internal/sku-collision",
          nowIso,
        }));
      }
    }
  }

  return out;
}

/** Orientation suggestions — given room dimensions + roomKind + door
 *  positions, recommend WHERE the anchor SKU should sit. The ontology field
 *  Room.facing carries the entry-wall direction; the placer in iter 8c will
 *  enforce these, but the agent surfaces them today so designers see the
 *  reasoning early. */
export function detectOrientationGuidance(graph: ProjectGraph, room: GraphNode, nowIso: string): AgentProposal[] {
  const kind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const minDim = attrNum(room, "minDimM");
  const skus = skusInRoom(graph, room.id);
  const out: AgentProposal[] = [];

  // Bedroom — bed against the longer wall, away from the door wall.
  if (kind.includes("bed") && skus.length === 0) {
    out.push(newSuggestion({
      agentId: "agent-orientation-bed",
      confidence: 0.86,
      about: { kind: "Room", id: room.id },
      ruleId: "R-orientation-bed-headboard",
      ruleTitle: "Bed headboard placement",
      severity: "info",
      fix: `Place the bed headboard against the longest wall facing the door — gives 0.6 m clearance on both sides per NKBA G17, and a clear sightline to entry per Tilley & Henry Dreyfuss (2002).`,
      citation: "NKBA-G17+Dreyfuss",
      nowIso,
    }));
  }

  // Living — sofa facing TV wall, never blocking the door swing.
  if (kind.includes("living") && skus.length === 0) {
    out.push(newSuggestion({
      agentId: "agent-orientation-sofa",
      confidence: 0.84,
      about: { kind: "Room", id: room.id },
      ruleId: "R-orientation-sofa-tv",
      ruleTitle: "Sofa orientation",
      severity: "info",
      fix: `Place the sofa with its back to the longest unbroken wall, facing the TV or focal wall. Maintain ≥ 0.8 m of clear walkway between sofa and door (Forlizzi/DiSalvo CHI 2007).`,
      citation: "Forlizzi-CHI2007",
      nowIso,
    }));
  }

  // Kitchen — sink under window when one exists; if minDim < 1.5 m, prefer
  // single-wall layout over island.
  if (kind.includes("kitchen") && minDim !== null && minDim < 1.5 && skus.length === 0) {
    out.push(newSuggestion({
      agentId: "agent-orientation-kitchen",
      confidence: 0.88,
      about: { kind: "Room", id: room.id },
      ruleId: "R-orientation-kitchen-layout",
      ruleTitle: "Kitchen layout for narrow room",
      severity: "info",
      fix: `"${room.label}" min dim ${minDim.toFixed(2)} m — too narrow for an island. NKBA G2 recommends single-wall or galley layout below 1.5 m. Place sink under the window when possible (natural light over the work zone).`,
      citation: "NKBA-G2",
      nowIso,
    }));
  }

  // Dining — table at center, chairs need 0.9 m pull-back clearance.
  if (kind.includes("dining") && skus.length === 0) {
    out.push(newSuggestion({
      agentId: "agent-orientation-dining",
      confidence: 0.83,
      about: { kind: "Room", id: room.id },
      ruleId: "R-orientation-dining-center",
      ruleTitle: "Dining table orientation",
      severity: "info",
      fix: `Center the dining table; reserve 0.9 m of clear space on every side so chairs can pull back without hitting walls (ANSI A117.1 §304 turning radius reference).`,
      citation: "ANSI-A117.1-304",
      nowIso,
    }));
  }

  return out;
}

// detectAll has moved — import { orchestrate } from "./orchestrator"
// directly. The registry in codegen.json#agents is the single source
// of truth; this file just holds the specialist implementations.
